(function () {
  'use strict';

  const root = window.CarturioRoot || window.Shopify?.routes?.root || '/';
  const PREPAID_CODE = 'PREPAID10';
  const RESTORE_KEY = 'carturioExpressCartRestoreV1';
  const RESTORE_MAX_AGE = 7 * 24 * 60 * 60 * 1000;
  let restoreInProgress = false;

  async function requestJson(path, options) {
    const config = {
      ...(options || {}),
      credentials: 'same-origin',
      headers: { Accept: 'application/json', ...(options?.headers || {}) }
    };
    const response = await fetch(root + path, config);
    let data = {};
    try { data = await response.json(); } catch (_) {}
    if (!response.ok) {
      throw new Error(data.description || data.message || 'Shopify could not complete this cart action.');
    }
    return data;
  }

  function collectDiscountCodes(cart) {
    const applications = [
      ...(Array.isArray(cart.cart_level_discount_applications) ? cart.cart_level_discount_applications : []),
      ...((Array.isArray(cart.items) ? cart.items : []).flatMap((item) =>
        (Array.isArray(item.line_level_discount_allocations) ? item.line_level_discount_allocations : [])
          .map((allocation) => allocation.discount_application)
          .filter(Boolean)
      ))
    ];

    return [...new Set(applications
      .filter((application) => application.type === 'discount_code')
      .map((application) => String(application.title || application.code || '').trim())
      .filter(Boolean))];
  }

  function makeCartSnapshot(cart) {
    const items = (Array.isArray(cart.items) ? cart.items : []).map((item) => {
      const savedItem = {
        id: String(item.variant_id),
        quantity: Number(item.quantity) || 1
      };
      if (item.properties && Object.keys(item.properties).length) {
        savedItem.properties = item.properties;
      }
      const sellingPlanId = item.selling_plan_allocation?.selling_plan?.id;
      if (sellingPlanId) savedItem.selling_plan = String(sellingPlanId);
      return savedItem;
    });

    return {
      items,
      note: cart.note || '',
      attributes: cart.attributes || {},
      discountCodes: collectDiscountCodes(cart)
    };
  }

  function toAddItem(item, quantity) {
    const addItem = {
      id: String(item.variant_id),
      quantity: Number(quantity) || 1
    };
    if (item.properties && Object.keys(item.properties).length) {
      addItem.properties = item.properties;
    }
    const sellingPlanId = item.selling_plan_allocation?.selling_plan?.id;
    if (sellingPlanId) addItem.selling_plan = String(sellingPlanId);
    return addItem;
  }

  function normalizeProperties(properties) {
    return Object.entries(properties || {})
      .map(([key, value]) => [key, String(value)])
      .sort(([a], [b]) => a.localeCompare(b));
  }

  function matchesExpressLine(cartItem, expected) {
    const cartSellingPlan = cartItem.selling_plan_allocation?.selling_plan?.id;
    return String(cartItem.variant_id) === String(expected.id) &&
      JSON.stringify(normalizeProperties(cartItem.properties)) ===
        JSON.stringify(normalizeProperties(expected.properties)) &&
      String(cartSellingPlan || '') === String(expected.selling_plan || '');
  }

  function getCartItemsToKeep(cart, expected) {
    let quantityToRemove = Number(expected.quantity) || 1;
    const itemsToKeep = [];

    (Array.isArray(cart.items) ? cart.items : []).forEach((cartItem) => {
      const quantity = Number(cartItem.quantity) || 1;
      if (quantityToRemove > 0 && matchesExpressLine(cartItem, expected)) {
        const removeFromLine = Math.min(quantity, quantityToRemove);
        quantityToRemove -= removeFromLine;
        const remaining = quantity - removeFromLine;
        if (remaining > 0) itemsToKeep.push(toAddItem(cartItem, remaining));
      } else {
        itemsToKeep.push(toAddItem(cartItem, quantity));
      }
    });

    return itemsToKeep;
  }

  async function clearCart() {
    await requestJson('cart/clear.js', { method: 'POST' });
  }

  async function addCartItems(items) {
    if (!items?.length) return;
    await requestJson('cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items })
    });
  }

  async function restoreCartSnapshot(snapshot, extraItems) {
    await clearCart();
    await addCartItems([...(snapshot.items || []), ...(extraItems || [])]);

    await requestJson('cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        note: snapshot.note || '',
        attributes: snapshot.attributes || {}
      })
    });

    if (snapshot.discountCodes?.length) {
      try {
        await requestJson('cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: snapshot.discountCodes.join(',') })
        });
      } catch (_) {
        // An expired or unavailable old code must not block cart restoration.
      }
    }
  }

  async function restoreCartAfterReturningFromCheckout() {
    if (restoreInProgress) return;
    let raw;
    try { raw = sessionStorage.getItem(RESTORE_KEY); } catch (_) { return; }
    if (!raw) return;

    let pending;
    try { pending = JSON.parse(raw); } catch (_) {
      sessionStorage.removeItem(RESTORE_KEY);
      return;
    }

    if (!pending?.snapshot || !pending?.expected || !pending.createdAt ||
        Date.now() - pending.createdAt > RESTORE_MAX_AGE) {
      sessionStorage.removeItem(RESTORE_KEY);
      return;
    }

    restoreInProgress = true;
    try {
      const cart = await requestJson('cart.js');
      // Remove only the temporary buy-now line; keep anything added while checkout was open.
      const extraItems = getCartItemsToKeep(cart, pending.expected);
      await restoreCartSnapshot(pending.snapshot, extraItems);
      sessionStorage.removeItem(RESTORE_KEY);
      window.location.reload();
    } catch (_) {
      // Keep the restore record so a later storefront page load can retry safely.
    } finally {
      restoreInProgress = false;
    }
  }

  function initializeCartRestore() {
    restoreCartAfterReturningFromCheckout();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeCartRestore, { once: true });
  } else {
    initializeCartRestore();
  }
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) initializeCartRestore();
  });

  function setStatus(button, message, isError) {
    const wrap = button.closest('.carturio-product-purchase-buttons');
    const status = wrap?.querySelector('[data-carturio-prepaid-status]');
    if (status) {
      status.hidden = !message;
      status.textContent = message || '';
      status.classList.toggle('is-error', Boolean(isError));
      return;
    }
    if (!message) return;
    let statusEl = button.parentElement?.querySelector('[data-carturio-prepaid-status]');
    if (!statusEl) {
      statusEl = document.createElement('p');
      statusEl.className = 'carturio-prepaid-checkout__status';
      statusEl.setAttribute('role', 'status');
      statusEl.setAttribute('aria-live', 'polite');
      statusEl.dataset.carturioPrepaidStatus = '';
      button.insertAdjacentElement('afterend', statusEl);
    }
    statusEl.textContent = message;
    statusEl.classList.toggle('is-error', Boolean(isError));
  }

  function setLoading(button, loading) {
    const label = button.querySelector('.carturio-prepaid-checkout__label');
    const loadingLabel = button.querySelector('.carturio-prepaid-checkout__loading');
    button.disabled = loading;
    button.setAttribute('aria-busy', loading ? 'true' : 'false');
    if (label) label.hidden = loading;
    if (loadingLabel) loadingLabel.hidden = !loading;
  }

  function buildSelectedItem(productForm, variantId, quantity) {
    const item = { id: String(variantId), quantity };
    const properties = {};

    productForm?.querySelectorAll('[name^="properties["]').forEach((field) => {
      if (!field.disabled && field.name && field.value !== '') {
        const propertyName = field.name.slice('properties['.length, -1);
        if (propertyName) properties[propertyName] = field.value;
      }
    });

    if (Object.keys(properties).length) item.properties = properties;

    const sellingPlan = productForm?.querySelector('[name="selling_plan"]');
    if (sellingPlan && !sellingPlan.disabled && sellingPlan.value) {
      item.selling_plan = String(sellingPlan.value);
    }
    return item;
  }

  async function startPrepaidCheckout(button) {
    if (button.dataset.carturioPrepaidBusy === '1') return;
    button.dataset.carturioPrepaidBusy = '1';

    const formId = button.getAttribute('data-product-form-id');
    const productForm = formId ? document.getElementById(formId) : null;
    const variantInput = productForm?.querySelector('input[name="id"]');
    const variantId = variantInput?.value;

    if (!variantId || variantInput.disabled) {
      delete button.dataset.carturioPrepaidBusy;
      setStatus(button, variantInput?.disabled
        ? 'This option is currently unavailable. Please choose another option.'
        : 'Please select an available product option first.', true);
      return;
    }

    const sectionId = button.getAttribute('data-section-id') || '';
    const quantityField = sectionId
      ? document.getElementById('Quantity-Form-' + sectionId)?.querySelector('input[name="quantity"]')
      : null;
    const requestedQuantity = Number.parseInt(quantityField?.value || '1', 10);
    const quantity = Number.isFinite(requestedQuantity) && requestedQuantity > 0 ? requestedQuantity : 1;
    const selectedItem = buildSelectedItem(productForm, variantId, quantity);

    setStatus(button, '', false);
    setLoading(button, true);

    let snapshot;
    let restoreRecordSaved = false;

    try {
      const currentCart = await requestJson('cart.js');
      snapshot = makeCartSnapshot(currentCart);

      sessionStorage.setItem(RESTORE_KEY, JSON.stringify({
        createdAt: Date.now(),
        expected: selectedItem,
        snapshot
      }));
      restoreRecordSaved = true;

      // Isolate the express order from the customer's ordinary cart.
      await clearCart();
      await addCartItems([selectedItem]);

      window.location.assign(
        root + 'discount/' + encodeURIComponent(PREPAID_CODE) + '?redirect=%2Fcheckout'
      );
    } catch (error) {
      if (restoreRecordSaved && snapshot) {
        try {
          const cart = await requestJson('cart.js');
          const extraItems = getCartItemsToKeep(cart, selectedItem);
          await restoreCartSnapshot(snapshot, extraItems);
          sessionStorage.removeItem(RESTORE_KEY);
        } catch (_) {
          // Keep the saved snapshot for the next storefront page load.
        }
      }
      setLoading(button, false);
      delete button.dataset.carturioPrepaidBusy;
      setStatus(button, error?.message || 'Checkout could not be opened. Please try again.', true);
    }
  }

  async function clearPrepaidCodeBeforeCod(button) {
    if (button.dataset.carturioCodPreparing === '1') return;

    button.dataset.carturioCodPreparing = '1';
    if (button instanceof HTMLButtonElement) {
      button.type = 'button';
      button.disabled = true;
    }
    button.setAttribute('aria-busy', 'true');

    try {
      const cart = await requestJson('cart.js');
      const appliedCodes = collectDiscountCodes(cart);
      const isPrepaidCode = (code) =>
        code.toUpperCase() === PREPAID_CODE ||
        code.toUpperCase() === 'CARTURIO PREPAID - 10% OFF';

      if (appliedCodes.some(isPrepaidCode)) {
        const remainingCodes = appliedCodes.filter((code) => !isPrepaidCode(code));
        await requestJson('cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: remainingCodes.join(',') })
        });
      }

      if (button instanceof HTMLButtonElement) button.disabled = false;
      button.removeAttribute('aria-busy');

      // Replay the EasySell click as a button, never as a Shopify form submit.
      button.dataset.carturioCodReplay = '1';
      button.click();
      delete button.dataset.carturioCodReplay;
      delete button.dataset.carturioCodPreparing;
    } catch (error) {
      if (button instanceof HTMLButtonElement) button.disabled = false;
      button.removeAttribute('aria-busy');
      delete button.dataset.carturioCodPreparing;

      let message = button.parentElement?.querySelector('[data-carturio-cod-discount-error]');
      if (!message) {
        message = document.createElement('p');
        message.className = 'carturio-cod-discount-error';
        message.setAttribute('role', 'alert');
        message.dataset.carturioCodDiscountError = '';
        button.insertAdjacentElement('afterend', message);
      }
      message.textContent = error?.message || 'Please try again before placing a COD order.';
    }
  }

  document.addEventListener('click', (event) => {
    const origin = event.target instanceof Element ? event.target : event.target?.parentElement;
    const prepaidButton = origin?.closest('[data-carturio-prepaid-checkout]');

    if (prepaidButton && !prepaidButton.disabled) {
      event.preventDefault();
      event.stopPropagation();
      startPrepaidCheckout(prepaidButton);
      return;
    }

    const codButton = origin?.closest(
      '.es-popup-button-product, .es-popup-button-overwrite, [data-carturio-easysell-cod]'
    );

    if (codButton) {
      if (codButton instanceof HTMLButtonElement) codButton.type = 'button';
      if (codButton.dataset.carturioCodReplay === '1') return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (codButton.dataset.carturioCodPreparing !== '1') {
        clearPrepaidCodeBeforeCod(codButton);
      }
    }
  }, true);
})();